import {h, Fragment} from "preact"
import { useFacet } from "@nosto/preact"
import { useMemo } from "preact/hooks"
import { materialFilters } from "../../config"

function MaterialFilter({ facet, value }) {
    const { toggleProductFilter } = useFacet(facet)
    const valueName = value.value.replaceAll(' ', '-').replaceAll('/', '-').trim().toLowerCase();
    const inputId = `facet-${facet.id}-filter-${valueName}`
    
    // Get the material filter data for this value
    const materialData = materialFilters[value.value] || {
        title: value.value,
        label: "",
        image: ""
    }

    return (
        <label class="block cursor-pointer" tabindex={0}>
            <input
                id={inputId}
                value={value.value}
                type="checkbox"
                class="peer hidden"
                checked={value.selected}
                onChange={(e) => {
                    e.preventDefault();
                    toggleProductFilter(facet.field, value.originalValue, !value.selected);
                }}
            />
            <div class="flex flex-col items-center border border-t-ui-lines-2 peer-checked:active peer-checked:border-t-foreground-secondary text-t-foreground peer-disabled:text-t-disabled peer-checked:text-t-foreground-secondary">
                <img
                    class="w-full h-auto"
                    src={materialData.image}
                    srcSet={`${materialData.image} 1x, ${materialData.image} 2x`}
                    sizes="132px"
                    alt={`Material: ${materialData.title}`}
                    loading="lazy"
                />
                <h5 class="caption text-center font-medium text-t-foreground-primary px-1 pt-2">{materialData.title}</h5>
                <div class="text-xs text-center text-t-foreground-primary px-1">({value.count})</div>
                <div class="text-xs text-center px-1 pb-1">{materialData.label}</div>
            </div>
        </label>
    )
}

export default function FacetMaterials({ facet }) {
    // Extract unique values from the array-based facet data
    const uniqueValues = useMemo(() => {
        if (!facet.data) return []
        
        // Flatten all arrays and get unique values
        const allValues = facet.data.flatMap(item => {
            let itemValues
            try {
                // Try to parse as JSON first (for stringified arrays)
                itemValues = JSON.parse(item.value)
            } catch {
                // If parsing fails, treat as regular value
                itemValues = item.value
            }
            
            // Handle both array and single value cases
            return Array.isArray(itemValues) ? itemValues : [itemValues]
        })
        
        // Get unique values and create facet data structure
        const unique = [...new Set(allValues)]
        
        // Create the facet data structure
        const facetData = unique.map(value => {
            // Find the original item that contains this value to get its count and original value
            const originalItem = facet.data.find(item => {
                let itemValues
                try {
                    itemValues = JSON.parse(item.value)
                } catch {
                    itemValues = item.value
                }
                const values = Array.isArray(itemValues) ? itemValues : [itemValues]
                return values.includes(value)
            })
            
            // Ensure the original value is always JSON-encoded for filtering
            let originalValue = originalItem ? originalItem.value : value;
            try {
                // If it's already JSON-encoded, use it as-is
                JSON.parse(originalValue);
            } catch {
                // If it's not JSON-encoded, wrap it in an array
                originalValue = JSON.stringify([value]);
            }
            
            return {
                value: value, // Parsed value for display
                originalValue: originalValue, // JSON-encoded value for filtering
                selected: facet.data.some(item => {
                    let itemValues
                    try {
                        itemValues = JSON.parse(item.value)
                    } catch {
                        itemValues = item.value
                    }
                    const values = Array.isArray(itemValues) ? itemValues : [itemValues]
                    return values.includes(value) && item.selected
                }),
                count: originalItem ? originalItem.count : 0
            }
        })
        
        // Sort by the order defined in materialFilters
        const materialOrder = Object.keys(materialFilters)
        return facetData.sort((a, b) => {
            const aIndex = materialOrder.indexOf(a.value)
            const bIndex = materialOrder.indexOf(b.value)
            
            // If both values are in materialFilters, sort by their order
            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex
            }
            // If only one is in materialFilters, prioritize it
            if (aIndex !== -1) return -1
            if (bIndex !== -1) return 1
            // If neither is in materialFilters, maintain original order
            return 0
        })
    }, [facet.data])

    return (
        <details class="js-filter border-b border-t-ui-lines-2 px-sm group/filter-dropdown" aria-labelledby={`facet-heading-${facet.id}`}>
            <summary class="ns-facet-heading focus-inset flow-root py-lg md:py-md cursor-pointer" data-nosto-element="facet">
                <div class="flex w-full items-center justify-between">
                    <h6 id={`facet-heading-${facet.id}`} class="caption-large uppercase">
                        {facet.name}
                    </h6>
                    <svg fill="none" class="icon icon-plus" viewBox="0 0 10 10" aria-label="Plus icon" role="presentation" focusable="false">
                        <path fill="currentColor" fill-rule="evenodd" d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z" clip-rule="evenodd"></path>
                    </svg>
                    <svg fill="none" class="icon icon-minus" id="icon-minus" viewBox="0 0 10 2" aria-label="Minus icon" role="presentation" focusable="false">
                        <path fill="currentColor" fill-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1" clip-rule="evenodd"></path>
                    </svg>
                </div>
            </summary>
            
            <div class="pb-6">
                <div class="pb-4 text-xs">All furniture features POLYWOOD, our proprietary climate-tested lumber.  Explore all styles and mixed-material options below.</div>
                <ul class="list-unstyled grid grid-cols-2 gap-xs" role="menu">
                    {uniqueValues.map(value => (
                        <MaterialFilter
                            key={`${value.value}-${facet.id}`}
                            facet={facet}
                            value={value}
                        />
                    ))}
                </ul>
            </div>
        </details>
    )
}
